// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Aggregate-mode renderer for SRM track findings.
 *
 * Reads the `findings` dataset slot across every document passed in via
 * `documentSlotData`, normalizes the payload, and renders a single merged
 * list (matching the legacy inline TrackCards rendering).
 */
import { type FC } from 'react'
import { AlertTriangle, CheckCircle2, FileText, Quote } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { RendererComponentProps } from '@/app-shell/renderer-components'
import type { DatasetResponse } from '@/subsystems/documents/services'
import type { ExposureLevel, SrmFinding } from '../types'

interface FindingsPayload {
  findings?: Array<{
    knowledge_item?: string
    knowledgeItem?: string
    content?: string
    section_reference?: string
    sectionReference?: string
    source_text?: string
    sourceText?: string
    exposure?: string
    recommendation?: string
    page_number?: number
    pageNumber?: number
    riskScore?: number | null
    risk_score?: number | null
  }>
}

function parseExposureLevel(exposure: string | null | undefined): ExposureLevel | null {
  if (!exposure) return null
  const match = exposure.match(/^(NEGLIGIBLE|LOW|MODERATE|HIGH|SEVERE|CRITICAL):/i)
  return match ? (match[1].toUpperCase() as ExposureLevel) : null
}

function transformFindings(payload: DatasetResponse | null): SrmFinding[] {
  const data = payload?.payload as FindingsPayload | undefined
  if (!data?.findings) return []
  return data.findings.map((f) => {
    const exposure = f.exposure ?? null
    return {
      kbItemId: f.knowledge_item ?? f.knowledgeItem ?? 'unknown',
      content: f.content ?? '',
      sectionReference: f.section_reference ?? f.sectionReference ?? null,
      sourceText: f.source_text ?? f.sourceText ?? null,
      exposure,
      exposureLevel: parseExposureLevel(exposure),
      recommendation: f.recommendation ?? null,
      pageNumber: f.page_number ?? f.pageNumber ?? null,
      riskScore: f.riskScore ?? f.risk_score ?? null,
    }
  })
}

export const SrmFindingsRenderer: FC<RendererComponentProps> = ({ documentSlotData = [] }) => {
  const findings: SrmFinding[] = documentSlotData.flatMap((d) =>
    transformFindings(d.slotData.findings ?? null),
  )
  // An empty list only means "no issues" once some document has actually been analyzed
  // for this dataset. A track can also hold documents whose review has not been run,
  // and claiming they are clean would be wrong.
  const analyzed = documentSlotData.some((d) => d.slotData.findings)

  if (findings.length === 0) {
    return analyzed ? (
      <div className="bg-green-500/5 px-4 py-3">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          <span className="font-medium">No findings</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 ml-6">
          Analysis complete — no issues detected
        </p>
      </div>
    ) : (
      <div className="bg-muted/10 px-4 py-3 text-sm text-muted-foreground">
        Not reviewed yet — run review to generate findings.
      </div>
    )
  }

  return (
    <div className="bg-muted/10 px-4 py-3 space-y-3">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Findings
      </div>
      <div className="space-y-3">
        {findings.map((finding, fidx) => (
          <div
            key={finding.kbItemId + fidx}
            className="rounded-lg bg-card border overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-muted/30 border-b">
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle
                  className={`h-4 w-4 shrink-0 ${
                    finding.exposureLevel === 'CRITICAL' || finding.exposureLevel === 'SEVERE'
                      ? 'text-destructive'
                      : finding.exposureLevel === 'HIGH' || finding.exposureLevel === 'MODERATE'
                        ? 'text-chart-1'
                        : 'text-muted-foreground/60'
                  }`}
                />
                <span className="text-sm font-medium text-foreground">
                  <span className="font-mono text-muted-foreground">KB-{finding.kbItemId}</span>
                  {finding.sectionReference && (
                    <span className="text-muted-foreground"> · {finding.sectionReference}</span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3 ml-auto shrink-0">
                {finding.riskScore !== null && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-muted-foreground">{finding.riskScore}/5</span>
                    <span className="text-[10px] text-muted-foreground/70">Risk</span>
                  </div>
                )}
                {finding.exposureLevel && (
                  <Badge
                    variant="outline"
                    className={`font-semibold text-xs ${
                      finding.exposureLevel === 'CRITICAL' || finding.exposureLevel === 'SEVERE'
                        ? 'border-destructive bg-destructive/10 text-destructive'
                        : finding.exposureLevel === 'HIGH'
                          ? 'border-chart-1 bg-chart-1/10 text-chart-1'
                          : finding.exposureLevel === 'MODERATE'
                            ? 'border-yellow-500 bg-yellow-500/10 text-yellow-600'
                            : 'border-green-500 bg-green-500/10 text-green-600'
                    }`}
                  >
                    {finding.exposureLevel}
                  </Badge>
                )}
              </div>
            </div>

            <div className="px-4 py-3 space-y-4">
              {finding.exposure && (
                <div
                  className={`rounded-md p-3 ${
                    finding.exposureLevel === 'CRITICAL' || finding.exposureLevel === 'SEVERE'
                      ? 'bg-destructive/5 border border-destructive/20'
                      : finding.exposureLevel === 'HIGH'
                        ? 'bg-chart-1/5 border border-chart-1/20'
                        : finding.exposureLevel === 'MODERATE'
                          ? 'bg-yellow-500/5 border border-yellow-500/20'
                          : 'bg-green-500/5 border border-green-500/20'
                  }`}
                >
                  <p className="text-sm text-foreground">
                    {finding.exposure.replace(/^(NEGLIGIBLE|LOW|MODERATE|HIGH|SEVERE|CRITICAL):\s*/i, '')}
                  </p>
                </div>
              )}

              {finding.sourceText && (
                <div className="flex gap-2">
                  <Quote className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm text-muted-foreground italic">
                      "{finding.sourceText}"
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
                      <FileText className="h-3 w-3" />
                      <span>
                        {finding.sectionReference || 'Document'}
                        {finding.pageNumber && ` · Page ${finding.pageNumber}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {finding.recommendation && (
                <div className="flex gap-2 bg-muted/50 rounded-md p-3">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-primary uppercase tracking-wider">
                      Recommendation
                    </div>
                    <p className="text-sm text-foreground">
                      {finding.recommendation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
